import { useState, useEffect } from "react";
import EmailCapacityBreakdown from "../email_capacity_breakdown";
import EmailAccountSevenDayChart from "../charts/email_account_seven_day_chart";
import { Tag, TagLabel } from "~/react/ui/tag";
import { Count } from "~/react/ui/count";
import { Skeleton } from "~/react/ui/skeleton";

type Props = {
  accountId: number;
};

type UsageData = {
  sent_today: number;
  sent_last_7_days: Array<{ date: string; count: number }>;
  daily_limit: number;
  available: number;
  scheduled: number;
};

type Campaign = {
  id: number;
  name: string;
  status: string;
  recipients: number;
  scheduled_messages: number;
};

export default function EmailAccountUsageTab({ accountId }: Props) {
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const response = await fetch(`/email_accounts/${accountId}/usage.json`, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        });

        if (response.ok) {
          const data = await response.json();
          setUsageData(data);
        }
      } catch (error) {
        console.error("Failed to fetch usage data:", error);
      } finally {
        setIsLoadingUsage(false);
      }
    };

    const fetchCampaigns = async () => {
      try {
        const response = await fetch(`/email_accounts/${accountId}/campaigns.json`, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        });

        if (response.ok) {
          const data = await response.json();
          setCampaigns(data.campaigns || []);
        }
      } catch (error) {
        console.error("Failed to fetch campaigns:", error);
      } finally {
        setIsLoadingCampaigns(false);
      }
    };

    fetchUsage();
    fetchCampaigns();
  }, [accountId]);

  const sent = usageData?.sent_today ?? 0;
  const limit = usageData?.daily_limit ?? 0;
  const scheduled = usageData?.scheduled ?? 0;
  const sevenDayData = usageData?.sent_last_7_days ?? [];

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return null;
      default:
        return null;
    }
  };

  return (
    <div className="p-0">
      <h3 className="text-md-medium mb-4">
        {i18n.t("js.email_accounts_center.email_account.usage.sending_activity")}
      </h3>

      <div className="mb-4">
        <strong className="text-xs-medium">{i18n.t("js.email_accounts_center.email_account.usage.today")}</strong>
        {isLoadingUsage ? (
          <div className="my-4">
            <Skeleton size="freeform" style={{ width: "100%", height: "0.6rem", borderRadius: "4px" }} className="mb-2" />
            <div className="d-flex justify-content-between">
              <Skeleton size="freeform" style={{ width: "80px", height: "40px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ width: "80px", height: "40px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ width: "80px", height: "40px", borderRadius: "4px" }} />
            </div>
          </div>
        ) : (
          <EmailCapacityBreakdown
            sent={sent}
            scheduled={scheduled}
            limit={limit}
            showLegend
            chartSize="lg"
            className="my-4"
          />
        )}
      </div>

      <div className="mb-6">
        <strong className="text-xs-medium">
          {i18n.t("js.email_accounts_center.email_account.usage.seven_day_history")}
        </strong>
        <div className="mt-3">
          {isLoadingUsage ? (
            <div className="d-flex gap-5 justify-content-between px-4 pt-1">
              <Skeleton size="freeform" style={{ flex: 1, height: "40px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ flex: 1, height: "40px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ flex: 1, height: "40px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ flex: 1, height: "40px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ flex: 1, height: "40px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ flex: 1, height: "40px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ flex: 1, height: "40px", borderRadius: "4px" }} />
            </div>
          ) : (
            <EmailAccountSevenDayChart data={sevenDayData} dailyLimit={limit} />
          )}
        </div>
      </div>

      <h3 className="text-md-medium d-flex align-items-center gap-1 mb-4 mt-8">
        {i18n.t("js.email_accounts_center.email_account.usage.campaigns_title")}
        {!isLoadingCampaigns && <Count number={campaigns.length.toString()} />}
      </h3>
      {isLoadingCampaigns ? (
        <div className="mt-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="lead-campaign">
              <Skeleton size="freeform" style={{ width: "60%", height: "16px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ width: "40%", height: "16px", borderRadius: "4px" }} />
              <Skeleton size="freeform" style={{ width: "80px", height: "16px", borderRadius: "4px" }} />
            </div>
          ))}
        </div>
      ) : campaigns.length > 0 ? (
        <ul className="lead-campaigns">
          {campaigns.map((campaign) => (
            <li key={campaign.id} className="lead-campaign">
              <div className="lead-campaign__heading">
                <a
                  className="lead-campaign__name"
                  data-turbo-frame="_top"
                  href={`/campaigns/${campaign.id}`}
                >
                  {campaign.name}
                </a>
                <div className="lead-campaign__metas">
                  {i18n.t("js.email_accounts_center.email_account.usage.campaign_meta", {
                    recipients: campaign.recipients,
                    scheduled_messages: campaign.scheduled_messages,
                  })}
                </div>
              </div>
              <Tag variant={getStatusVariant(campaign.status)}>
                <TagLabel>
                  {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                </TagLabel>
              </Tag>
            </li>
          ))}
        </ul>
      ) : (
        <div className="h-hint">
          {i18n.t("js.email_accounts_center.email_account.usage.campaigns_placeholder")}
        </div>
      )}
    </div>
  );
}

